import { router } from '@inertiajs/react'
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import PriorityDropdown from './PriorityDropdown'
import TopicContextMenu from './TopicContextMenu'
import { TOPIC_CATEGORIES } from '../../types'
import type { Topic } from '../../types'

interface Props {
  topic: Topic
  index: number
  teamId: number
  canDrag: boolean
  onEdit: (topic: Topic) => void
  onResolve: (topic: Topic) => void
  onConvert: (topic: Topic) => void
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  onPriority?: (topic: Topic, value: boolean) => void
  onEdge?: (topic: Topic, edge: 'top' | 'bottom') => void
}

export default function TopicRow({
  topic, index, teamId, canDrag,
  onEdit, onResolve, onConvert, dragHandleProps, onPriority, onEdge
}: Props) {
  const handlePriorityChange = (priority: boolean) => {
    if (onPriority) return onPriority(topic, priority)
    router.put(`/teams/${teamId}/topics/${topic.id}`, {
      topic: { priority }
    }, { preserveScroll: true, preserveState: true })
  }

  const categoryLabel = topic.category && topic.category !== 'none'
    ? (TOPIC_CATEGORIES.find(c => c.value === topic.category)?.label
       ?? topic.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    : null

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 bg-white border border-stone rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
      onClick={() => onEdit(topic)}
    >
      {canDrag && (
        <div
          className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-secondary flex-shrink-0"
          {...(dragHandleProps || {})}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to reorder"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <rect x="8" y="4" width="3" height="3" rx="0.5" />
            <rect x="13" y="4" width="3" height="3" rx="0.5" />
            <rect x="8" y="10.5" width="3" height="3" rx="0.5" />
            <rect x="13" y="10.5" width="3" height="3" rx="0.5" />
            <rect x="8" y="17" width="3" height="3" rx="0.5" />
            <rect x="13" y="17" width="3" height="3" rx="0.5" />
          </svg>
        </div>
      )}

      <span className="text-sm font-bold text-text-muted w-5 text-center flex-shrink-0">
        {index + 1}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <p className="text-base font-semibold text-text-primary truncate">{topic.title}</p>
          {topic.priority && <span className="text-xs font-bold text-text-primary">PRIORITY</span>}
          {topic.owner && (
            <span className="text-sm font-medium text-text-secondary flex-shrink-0">
              {topic.owner.first_name} {topic.owner.last_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {categoryLabel && (
            <span className="text-xs text-text-muted">{categoryLabel}</span>
          )}
          {topic.created_at && (
            <span className="text-xs text-text-muted">
              Created {new Date(topic.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
          {topic.topic_type === 'long_term' && (
            <span className="text-xs font-bold text-text-primary bg-[#351E1D]/10 px-1.5 py-0.5 rounded">Camp</span>
          )}
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        {/* Stop propagation so toggling priority doesn't also open the row's edit modal */}
        <span onClick={(e) => e.stopPropagation()}>
          <PriorityDropdown value={topic.priority} onChange={handlePriorityChange} />
        </span>
      </div>

      {topic.owner && (
        <div
          className="w-8 h-8 rounded-full bg-charcoal text-white text-sm font-bold flex items-center justify-center flex-shrink-0"
          title={`${topic.owner.first_name} ${topic.owner.last_name}`}
        >
          {topic.owner.first_name?.charAt(0)}{topic.owner.last_name?.charAt(0)}
        </div>
      )}

      <div onClick={(e) => e.stopPropagation()}>
        <TopicContextMenu
          topic={topic}
          teamId={teamId}
          onPriority={() => handlePriorityChange(!topic.priority)}
          onEdge={onEdge ? edge => onEdge(topic, edge) : undefined}
          onEdit={() => onEdit(topic)}
          onResolve={() => onResolve(topic)}
          onConvert={() => onConvert(topic)}
        />
      </div>
    </div>
  )
}
